import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Grid, Row, Col, Panel, Button, Label, Tabs, Tab, FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import dateFormat from 'dateformat';
import { fetchApi } from '../../Network/NetworkService';

const STATUS_LABELS = {
    completed: { text: 'Envoyé', style: 'success' },
    failed: { text: 'Échec', style: 'danger' },
    stale: { text: 'Périmé', style: 'warning' },
    pending: { text: 'Programmé', style: 'info' },
    running: { text: 'En cours', style: 'info' },
    unknown: { text: 'Inconnu', style: 'default' }
};

const STATE_LABELS = {
    incomplete: { text: 'incomplet', style: 'warning' },
    waiting: { text: 'en attente', style: 'info' },
    approved: { text: 'approuvé', style: 'primary' },
    toCall: { text: 'à appeler', style: 'warning' },
    reviewed: { text: 'relu', style: 'success' },
    canceled: { text: 'annulé', style: 'default' },
    refunded: { text: 'remboursé', style: 'default' },
    terminated: { text: 'terminé', style: 'success' }
};

const toDateInput = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    const pad = (n) => String(n).padStart(2, '0');
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const renderStatus = (s) => {
    const m = STATUS_LABELS[s] || STATUS_LABELS.unknown;
    return <Label bsStyle={m.style}>{m.text}</Label>;
};

const renderState = (s) => {
    if (!s) return <span className="text-muted">—</span>;
    const m = STATE_LABELS[s] || { text: s, style: 'default' };
    return <Label bsStyle={m.style}>{m.text}</Label>;
};

const renderRow = (r, hasResponse) => {
    const detailHref = r.entityKind === 'Doctor'
        ? `/doctors/detail/${r.entityId}`
        : `/requests/detail/${r.entityId}`;
    // For Doctor-entity rows, `r.user` IS the doctor — link to the doctor
    // profile, not /users/detail/<doctorId>.
    const recipientHref = r.entityKind === 'Doctor'
        ? `/doctors/detail/${r.entityId}`
        : (r.user && r.user._id ? `/users/detail/${r.user._id}` : null);
    return (
        <tr key={String(r._id)}>
            <td>{r.sentAt ? dateFormat(r.sentAt, 'dd/mm HH:MM') : '—'}</td>
            <td>{renderStatus(r.agendaStatus)}</td>
            <td>
                {r.user ? (
                    recipientHref
                        ? <Link to={recipientHref}>{r.user.name || r.user.email || 'Recipient'}</Link>
                        : <span>{r.user.name || r.user.email || 'Recipient'}</span>
                ) : <span className="text-muted">—</span>}
            </td>
            <td>
                {r.user && (
                    <small>
                        {r.user.email && <div>{r.user.email}</div>}
                        {r.user.phone && <div>{r.user.phone}</div>}
                    </small>
                )}
            </td>
            <td>{r.entityKind === 'Request' ? renderState(r.entity && r.entity.state) : <span className="text-muted">docteur</span>}</td>
            {hasResponse && (
                <td style={{ textAlign: 'center' }}>
                    {r.responded
                        ? <Label bsStyle="success">Oui</Label>
                        : <Label bsStyle="default">Non</Label>}
                </td>
            )}
            <td>
                {r.failReason && <small className="text-danger">{r.failReason}</small>}
            </td>
            <td>
                <Link to={detailHref} className="btn btn-info btn-xs">Voir</Link>
            </td>
        </tr>
    );
};

const FollowupEmails = () => {
    const today = useMemo(() => new Date(), []);
    const monthAgo = useMemo(() => new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000), [today]);

    const [jobs, setJobs] = useState([]);
    const [job, setJob] = useState('');
    const [from, setFrom] = useState(toDateInput(monthAgo));
    const [to, setTo] = useState(toDateInput(today));
    const [rows, setRows] = useState([]);
    const [jobMeta, setJobMeta] = useState({ hasResponse: false, responseLabel: null, label: '' });
    const [summary, setSummary] = useState({ sent: 0, completed: 0, failed: 0, stale: 0, pending: 0 });
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('all');

    useEffect(() => {
        fetchApi('/followup-emails/jobs', 'GET')
            .then((res) => {
                if (res && res.success) {
                    const list = res.data.jobs || [];
                    setJobs(list);
                    if (list.length > 0) setJob(list[0].name);
                }
            })
            .catch(() => {});
    }, []);

    const fetchList = useCallback(async () => {
        if (!job) return;
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                job,
                from: new Date(`${from}T00:00:00`).toISOString(),
                to: new Date(`${to}T23:59:59.999`).toISOString(),
                limit: '500'
            });
            const res = await fetchApi(`/followup-emails/list?${params.toString()}`, 'GET');
            if (res && res.success) {
                setRows(res.data.rows || []);
                setSummary(res.data.summary || {});
                setTotal(res.data.total || 0);
                setJobMeta(res.data.job || {});
            } else {
                setError((res && res.message) || 'Erreur de chargement');
            }
        } catch (err) {
            setError(`Erreur: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [job, from, to]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const failedRows = useMemo(
        () => rows.filter(r => r.agendaStatus === 'failed' || r.agendaStatus === 'stale'),
        [rows]
    );
    const noResponseRows = useMemo(
        // Mirror the backend summarize() guard — Doctor-entity rows have no
        // `state`, so an `undefined !== 'canceled'` check would always pass.
        () => rows.filter(r =>
            !r.responded
            && r.entityKind === 'Request'
            && r.entity
            && r.entity.state !== 'canceled'
            && r.entity.state !== 'refunded'
        ),
        [rows]
    );

    const renderTable = (data, emptyText) => {
        if (loading) return <p className="text-muted">Chargement...</p>;
        if (data.length === 0) return <p className="text-muted">{emptyText}</p>;
        return (
            <table className="table table-bordered table-hover table-condensed">
                <thead>
                    <tr>
                        <th>Envoyé le</th>
                        <th>Statut job</th>
                        <th>Destinataire</th>
                        <th>Contact</th>
                        <th>État</th>
                        {jobMeta.hasResponse && <th style={{ textAlign: 'center' }}>{jobMeta.responseLabel || 'Réponse'}</th>}
                        <th>Erreur</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>{data.map(r => renderRow(r, jobMeta.hasResponse))}</tbody>
            </table>
        );
    };

    const truncatedNotice = total > rows.length && (
        <p className="text-muted">
            <small>Affichage des {rows.length} plus récents sur {total} envoyés sur la période. Resserrer les dates pour tout voir.</small>
        </p>
    );

    const respondedCount = summary.responded || 0;
    const noResponseCount = summary.noResponse || 0;

    return (
        <Grid fluid>
            <Row>
                <Col xs={12}>
                    <h2>Emails de relance / suivi</h2>
                    <p className="text-muted">
                        Suivi unifié de tous les emails programmés via Agenda. Source : <code>agendajobarchives</code>,
                        enrichi avec le statut live de <code>agendaJobs</code> et l'état de l'entité ciblée.
                    </p>
                </Col>
            </Row>

            {error && (
                <Row className="mb-3">
                    <Col xs={12}>
                        <div className="alert alert-danger">{error}</div>
                    </Col>
                </Row>
            )}

            <Row className="mb-3">
                <Col md={4}>
                    <FormGroup>
                        <ControlLabel>Type d'email</ControlLabel>
                        <FormControl componentClass="select" value={job} onChange={(e) => setJob(e.target.value)}>
                            {jobs.length === 0 && <option value="">Chargement...</option>}
                            {jobs.map(j => (
                                <option key={j.name} value={j.name}>{j.label}</option>
                            ))}
                        </FormControl>
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <FormGroup>
                        <ControlLabel>Du</ControlLabel>
                        <FormControl type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <FormGroup>
                        <ControlLabel>Au</ControlLabel>
                        <FormControl type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                    </FormGroup>
                </Col>
                <Col md={2} style={{ paddingTop: 25 }}>
                    <Button bsStyle="primary" onClick={fetchList} disabled={loading} block>
                        {loading ? '...' : 'Actualiser'}
                    </Button>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={3}>
                    <Panel><Panel.Body>
                        <h5 className="text-muted" style={{ marginTop: 0 }}>Envoyés (page)</h5>
                        <h2 style={{ margin: 0 }}>{summary.sent}</h2>
                        {total > summary.sent && <small className="text-muted">sur {total} dans la période</small>}
                    </Panel.Body></Panel>
                </Col>
                <Col md={3}>
                    <Panel><Panel.Body>
                        <h5 className="text-muted" style={{ marginTop: 0 }}>Échecs / périmés</h5>
                        <h2 style={{ margin: 0, color: '#d9534f' }}>{(summary.failed || 0) + (summary.stale || 0)}</h2>
                        <small className="text-muted">{summary.failed || 0} échec · {summary.stale || 0} périmé</small>
                    </Panel.Body></Panel>
                </Col>
                {jobMeta.hasResponse ? (
                    <>
                        <Col md={3}>
                            <Panel><Panel.Body>
                                <h5 className="text-muted" style={{ marginTop: 0 }}>{jobMeta.responseLabel || 'Réponse positive'}</h5>
                                <h2 style={{ margin: 0, color: '#5cb85c' }}>{respondedCount}</h2>
                                <small className="text-muted">
                                    {summary.sent > 0 ? `${Math.round((respondedCount / summary.sent) * 100)}% de réponse` : '—'}
                                </small>
                            </Panel.Body></Panel>
                        </Col>
                        <Col md={3}>
                            <Panel><Panel.Body>
                                <h5 className="text-muted" style={{ marginTop: 0 }}>Sans réponse</h5>
                                <h2 style={{ margin: 0, color: '#f0ad4e' }}>{noResponseCount}</h2>
                            </Panel.Body></Panel>
                        </Col>
                    </>
                ) : (
                    <Col md={6}>
                        <Panel><Panel.Body>
                            <h5 className="text-muted" style={{ marginTop: 0 }}>Réponse</h5>
                            <p className="text-muted" style={{ margin: 0 }}>
                                <small>Pas de signal de réponse défini pour ce type d'email.</small>
                            </p>
                        </Panel.Body></Panel>
                    </Col>
                )}
            </Row>

            <Row>
                <Col xs={12}>
                    <Panel>
                        <Panel.Body style={{ overflowX: 'auto' }}>
                            <Tabs id="followup-emails-tabs" activeKey={activeTab} onSelect={setActiveTab}>
                                <Tab eventKey="all" title={`Tous (${total > rows.length ? `${rows.length}/${total}` : rows.length})`}>
                                    <div style={{ paddingTop: 15 }}>
                                        {truncatedNotice}
                                        {renderTable(rows, 'Aucun envoi sur cette période.')}
                                    </div>
                                </Tab>
                                <Tab eventKey="failed" title={`Échecs (${failedRows.length})`}>
                                    <div style={{ paddingTop: 15 }}>
                                        <p className="text-muted">
                                            <small>
                                                Jobs où l'agenda a retourné <code>failed</code> ou <code>stale</code>.
                                                Note : un statut <code>completed</code> ne garantit pas la livraison —
                                                les bounces SES ne sont pas tracés ici.
                                            </small>
                                        </p>
                                        {renderTable(failedRows, 'Aucun échec sur cette période.')}
                                    </div>
                                </Tab>
                                {jobMeta.hasResponse && (
                                    <Tab eventKey="noresponse" title={`Sans réponse (${noResponseRows.length})`}>
                                        <div style={{ paddingTop: 15 }}>
                                            <p className="text-muted">
                                                <small>Destinataires qui ont reçu l'email mais pas de signal de réponse. Requêtes annulées/remboursées exclues.</small>
                                            </p>
                                            {renderTable(noResponseRows, 'Aucun destinataire sans réponse.')}
                                        </div>
                                    </Tab>
                                )}
                            </Tabs>
                        </Panel.Body>
                    </Panel>
                </Col>
            </Row>
        </Grid>
    );
};

export default FollowupEmails;
